import { VALID_PHONE_NUMBER_REGEX_STRICT } from '@payaca/constants';
import {
  AUTOMATION_ACTION_TITLE_MAX_LENGTH,
  AUTOMATION_TITLE_MAX_LENGTH,
} from '@payaca/constants/automation';
import {
  COMMA_SEPARATED_EMAIL_LIST,
  VALID_EMAIL_REGEX,
} from '@payaca/constants/regexConstants';
import type {
  Automation,
  AutomationActionTypes,
  EmailNotificationActionConfig,
  SMSActionConfig,
} from '@payaca/types/automationTypes';
import type { FieldValidator } from '@payaca/types/fieldValidationTypes';
import { get } from 'lodash-es';
import {
  getAllowEmptyValidator,
  getConditionalValidator,
  getIsRequiredFieldValidator,
  getLengthFieldValidator,
  getRegexMatchFieldValidator,
} from './fieldValidationHelper';
import { validateForm } from './formValidationHelper';

const getActionConfigValidators = <TFormState extends Record<string, any>>(
  actionAccessKey: string
) => {
  const emailNotificationActionConfigValidators =
    getEmailNotificationActionConfigValidators((formState: TFormState) => {
      return get(formState, actionAccessKey)?.type === 'E-mail Notification'
        ? (get(formState, actionAccessKey)
            .config as EmailNotificationActionConfig)
        : undefined;
    });

  const smsNotificationActionConfigValidators =
    getSmsNotificationActionConfigValidators((formState: TFormState) => {
      return get(formState, actionAccessKey)?.type === 'SMS Notification'
        ? (get(formState, actionAccessKey).config as SMSActionConfig)
        : undefined;
    });

  const validators: Record<string, FieldValidator<TFormState>[]> = {};

  Object.entries(emailNotificationActionConfigValidators).forEach(
    ([key, value]) => {
      if (!(`${actionAccessKey}.config.${key}` in validators)) {
        validators[`${actionAccessKey}.config.${key}`] = [];
      }

      validators[`${actionAccessKey}.config.${key}`].push(
        ...value.map((v) => {
          return getConditionalValidator<TFormState>(
            (formState: TFormState) => {
              return (
                get(formState, actionAccessKey)?.type === 'E-mail Notification'
              );
            },
            v
          );
        })
      );
    }
  );

  Object.entries(smsNotificationActionConfigValidators).forEach(
    ([key, value]) => {
      if (!(`${actionAccessKey}.config.${key}` in validators)) {
        validators[`${actionAccessKey}.config.${key}`] = [];
      }

      validators[`${actionAccessKey}.config.${key}`].push(
        ...value.map((v) =>
          getConditionalValidator<TFormState>(
            (formState: TFormState) =>
              get(formState, actionAccessKey)?.type === 'SMS Notification',
            v
          )
        )
      );
    }
  );

  return validators;
};

const getEmailNotificationActionConfigValidators = <
  TFormState extends Record<string, any>,
>(
  getConfigFromFormState: (
    formState: TFormState
  ) => EmailNotificationActionConfig | undefined
) => {
  return {
    sendToEmail: [getIsRequiredFieldValidator()],
    customEmail: [
      getConditionalValidator<TFormState>(
        (formState: TFormState) =>
          getConfigFromFormState(formState)?.sendToEmail ===
          'customEmailAddress',
        getRegexMatchFieldValidator<TFormState>(VALID_EMAIL_REGEX)
      ),
    ],
    emailSubject: [getIsRequiredFieldValidator()],
    emailBody: [
      getConditionalValidator((formState: TFormState) => {
        return !getConfigFromFormState(formState)?.emailTemplatePublicId;
      }, getIsRequiredFieldValidator()),
    ],
    ccEmails: [
      getAllowEmptyValidator(
        getRegexMatchFieldValidator(COMMA_SEPARATED_EMAIL_LIST, {
          customErrorMessage: 'This must be a comma separated list of emails',
        })
      ),
    ],
    bccEmails: [
      getAllowEmptyValidator(
        getRegexMatchFieldValidator(COMMA_SEPARATED_EMAIL_LIST, {
          customErrorMessage: 'This must be a comma separated list of emails',
        })
      ),
    ],
  };
};

const getSmsNotificationActionConfigValidators = <
  TFormState extends Record<string, any>,
>(
  getConfigFromFormState: (formState: TFormState) => SMSActionConfig | undefined
) => {
  return {
    sendToNumber: [getIsRequiredFieldValidator()],
    messageBody: [getIsRequiredFieldValidator()],
    customNumber: [
      getConditionalValidator<TFormState>(
        (formState: TFormState) =>
          getConfigFromFormState(formState)?.sendToNumber ===
          'customTelephoneNumber',
        getRegexMatchFieldValidator<TFormState>(VALID_PHONE_NUMBER_REGEX_STRICT)
      ),
    ],
  };
};

export const getFieldValidatorsForCustomAutomation = ({
  conditionsCount,
  actionsCount,
}: {
  conditionsCount: number;
  actionsCount: number;
}): Record<
  string,
  FieldValidator<Partial<Automation<AutomationActionTypes>>>[]
> => {
  const validators: Record<
    string,
    FieldValidator<Partial<Automation<AutomationActionTypes>>>[]
  > = {
    title: [
      getIsRequiredFieldValidator(),
      getLengthFieldValidator({ min: 0, max: AUTOMATION_TITLE_MAX_LENGTH }),
    ],
  };

  // conditions
  for (let i = 0; i < conditionsCount; i++) {
    validators[`conditions[${i}].field`] = [getIsRequiredFieldValidator()];
    validators[`conditions[${i}].operator`] = [getIsRequiredFieldValidator()];
    validators[`conditions[${i}].value`] = [getIsRequiredFieldValidator()];
  }

  //actions
  for (let i = 0; i < actionsCount; i++) {
    const v = getActionConfigValidators(`actions[${i}]`);
    Object.entries(v).forEach(([key, value]) => {
      validators[key] = value;
    });
    validators[`actions[${i}].title`] = [
      getIsRequiredFieldValidator(),
      getLengthFieldValidator({
        min: 0,
        max: AUTOMATION_ACTION_TITLE_MAX_LENGTH,
      }),
    ];
  }

  return validators;
};

export const isCustomAutomationValid = (
  automation: Partial<Automation<AutomationActionTypes>>
): boolean => {
  const validators = getFieldValidatorsForCustomAutomation({
    conditionsCount: automation.conditions?.length || 0,
    actionsCount: automation.actions?.length || 0,
  });

  const result = validateForm(automation, validators);

  return Object.values(result).every((x) => x.isValid);
};
